import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Select, Tag, Collapse, Space, Typography, Switch, Divider, Empty, Spin, Modal, Input } from 'antd';
import { CopyOutlined, QuestionCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Button } from '@app/components/common/buttons/Button/Button';
import { notificationController } from '@app/controllers/notificationController';
import {
  disableDebugSession,
  enableDebugSession,
  enqueueDebugAction,
  getActiveDebugSession,
  getPastDebugSessions,
  getFirmwares,
} from '@app/api/admin.api';
import { DebugAction, DebugActionStatus, DebugSession } from '@app/models/DebugSessionModel';
import { FirmwareModel } from '@app/pages/Firmwares/types';

const POLL_MS = 5000;
const AGENT_STORAGE_KEY = 'debug_agent_identifier';

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'When should I enable debug mode?',
    a: 'Only while you are on a live call or chat with this customer. It turns on remote troubleshooting on their personal app — always turn it off when you are done.',
  },
  {
    q: 'What does the customer see when I enable it?',
    a: 'They get a push notification asking them to open the app, and a "Support mode active" banner stays at the top of their screen for the whole session.',
  },
  {
    q: 'Nothing is happening / status says "never polled". What do I do?',
    a: 'Ask the customer to fully close the app (swipe it away) and open it again, then keep it open and stay near their cushion with Bluetooth on. The app only checks for support actions while it is open and connected.',
  },
  {
    q: 'How do diagnostics run?',
    a: 'Click "Run diagnostics". Once the customer\'s app is open and connected, it reads the cushion automatically and the results appear here — the customer does not need to do anything.',
  },
  {
    q: 'How do I update or downgrade firmware?',
    a: 'Pick the version and click Install. The customer must tap "Allow" in the app. You can select an older version to downgrade.',
  },
  {
    q: 'The update won\'t start — what should I check?',
    a: 'The cushion must be connected over Bluetooth, plugged into power (charging), and powered OFF. If it is on, ask the customer to turn it off first.',
  },
  {
    q: 'Why does the customer have to tap "Allow" for updates?',
    a: 'A firmware update changes the cushion, so we require the customer\'s consent. Diagnostics and data sync are read-only and run without a prompt.',
  },
  {
    q: 'When does the session end?',
    a: 'Turn the Debug mode switch off when you finish. It also auto-expires after about 2 hours as a safety net.',
  },
  {
    q: 'Does this affect the customer\'s data?',
    a: 'No. Diagnostics and sync only read from the cushion. Only a firmware update changes the device, and that always needs the customer to tap "Allow".',
  },
];

interface Props {
  userId: number;
  userName?: string;
  open: boolean;
  onClose: () => void;
}

const statusColor = (status: DebugActionStatus): string => {
  switch (status) {
    case 'succeeded':
      return 'green';
    case 'failed':
    case 'timed_out':
    case 'expired':
      return 'red';
    case 'canceled':
      return 'default';
    case 'running':
    case 'dispatched':
      return 'processing';
    default:
      return 'gold';
  }
};

const secondsAgo = (iso: string | null): number | null => {
  if (!iso) return null;
  return dayjs().diff(dayjs(iso), 'second');
};

// Manual relative-future string (the relativeTime dayjs plugin isn't loaded globally).
const relativeFuture = (iso: string | null): string => {
  if (!iso) return '';
  const minutes = dayjs(iso).diff(dayjs(), 'minute');
  if (minutes <= 0) return 'soon';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `in ${hours} hr`;
};

// Builds a plain-text, copy-friendly report of a session's outcome.
const buildReport = (session: DebugSession, userName?: string): string => {
  const who = userName || session.user?.name || session.user?.email || `user #${session.user?.id}`;
  const lines: string[] = [];
  lines.push(`Troubleshooting session — ${who}`);
  lines.push(`Agent: ${session.agent_identifier || 'unknown'}`);
  lines.push(`Started: ${dayjs(session.created_at).format('MMM D, YYYY h:mm A')}`);
  if (session.last_known_firmware) lines.push(`Firmware: ${session.last_known_firmware}`);
  lines.push('');

  const ordered = [...(session.actions || [])].sort((a, b) => a.sequence - b.sequence);
  if (!ordered.length) {
    lines.push('No actions were run.');
    return lines.join('\n');
  }

  ordered.forEach((a) => {
    const when = a.completed_at || a.created_at;
    lines.push(
      `#${a.sequence} ${a.action_type_display} — ${a.status_display}` +
        (when ? ` (${dayjs(when).format('MMM D, h:mm:ss A')})` : ''),
    );
    if (a.error) lines.push(`  Error: ${a.error}`);
    const r = a.result;
    if (r) {
      if (Array.isArray(r.diagnostics)) {
        r.diagnostics.forEach((d) => lines.push(`  ${d.command}\t${d.note}`));
      }
      if (r.health) {
        lines.push(`  Device/app: ${Object.entries(r.health).map(([k, v]) => `${k}=${v}`).join(', ')}`);
      }
      if (r.installed) lines.push(`  Installed firmware v${r.firmware ?? r.targetVersion}`);
      if (r.synced) lines.push('  Cushion data synced.');
    }
    lines.push('');
  });
  return lines.join('\n').trimEnd();
};

const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    notificationController.success({ message: 'Results copied to clipboard.' });
  } catch (e) {
    notificationController.error({ message: 'Could not copy to clipboard.' });
  }
};

// Renders the batch result document an action produced.
const ActionResult: React.FC<{ action: DebugAction }> = ({ action }) => {
  if (action.error) {
    return <Typography.Text type="danger">Error: {action.error}</Typography.Text>;
  }
  const result = action.result;
  if (!result) {
    return <Typography.Text type="secondary">No result.</Typography.Text>;
  }

  return (
    <div>
      {Array.isArray(result.diagnostics) && result.diagnostics.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {result.diagnostics.map((d, i) => (
              <tr key={i} style={{ borderBottom: 'var(--soft-panel-border)' }}>
                <td
                  style={{
                    padding: '6px 10px 6px 0',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'top',
                    opacity: 0.7,
                  }}
                >
                  {d.command}
                </td>
                <td style={{ padding: '6px 0', verticalAlign: 'top' }}>{d.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {result.health && (
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Device / app
          </Typography.Text>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(result.health).map(([k, v]) => (
              <Tag key={k}>
                {k}: {String(v)}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {result.installed && (
        <Typography.Text>Installed firmware v{result.firmware ?? result.targetVersion}</Typography.Text>
      )}
      {result.synced && <Typography.Text>Cushion data synced.</Typography.Text>}
    </div>
  );
};

// Shared collapse of a session's actions (used for current + past sessions).
const SessionResults: React.FC<{ actions: DebugAction[]; openFirst?: boolean }> = ({ actions, openFirst }) => {
  if (!actions || !actions.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No actions" />;
  }
  const ordered = [...actions].reverse();
  return (
    <Collapse style={{ marginTop: 8 }} defaultActiveKey={openFirst ? [String(ordered[0].id)] : []}>
      {ordered.map((action) => {
        const when = action.completed_at || action.created_at;
        return (
          <Collapse.Panel
            key={String(action.id)}
            header={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Space size={8} wrap>
                  <Tag>#{action.sequence}</Tag>
                  <Typography.Text strong>{action.action_type_display}</Typography.Text>
                  <Tag color={statusColor(action.status)}>{action.status_display}</Tag>
                  {action.requires_confirmation && !action.confirmed_at && action.status !== 'expired' && (
                    <Tag color="gold">awaiting user tap</Tag>
                  )}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {when ? dayjs(when).format('MMM D, h:mm:ss A') : ''}
                </Typography.Text>
              </div>
            }
          >
            <ActionResult action={action} />
          </Collapse.Panel>
        );
      })}
    </Collapse>
  );
};

const TroubleshootingDrawer: React.FC<Props> = ({ userId, userName, open, onClose }) => {
  const [session, setSession] = useState<DebugSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [firmwares, setFirmwares] = useState<FirmwareModel[]>([]);
  const [selectedFirmware, setSelectedFirmware] = useState<number | undefined>(undefined);
  const inFlight = useRef(false);

  // Agent-identity prompt shown before enabling a session.
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentInput, setAgentInput] = useState('');

  // Agent guide / FAQ popup.
  const [faqOpen, setFaqOpen] = useState(false);

  // Past (inactive) sessions, lazily loaded when the section is expanded.
  const [pastSessions, setPastSessions] = useState<DebugSession[]>([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await getActiveDebugSession(userId);
      setSession(data);
    } catch {
      // transient; keep last known session
    } finally {
      inFlight.current = false;
    }
  }, [userId]);

  // Initial load + firmware list when the drawer opens.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPastLoaded(false);
    setPastSessions([]);
    Promise.all([getActiveDebugSession(userId), getFirmwares()])
      .then(([s, fws]) => {
        setSession(s);
        setFirmwares(fws || []);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [open, userId]);

  // Poll while open and a session is active for near-live results.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [open, refresh]);

  const loadPastSessions = useCallback(
    async (keys: string | string[]) => {
      const active = Array.isArray(keys) ? keys : [keys];
      if (!active.includes('past') || pastLoaded) return;
      setPastLoading(true);
      try {
        const data = await getPastDebugSessions(userId);
        // Most recent first.
        setPastSessions([...data].sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()));
        setPastLoaded(true);
      } catch {
        // ignore; can retry by collapsing/expanding
      } finally {
        setPastLoading(false);
      }
    },
    [userId, pastLoaded],
  );

  const isActive = !!session?.is_active;

  const openEnablePrompt = () => {
    let last = '';
    try {
      last = window.localStorage.getItem(AGENT_STORAGE_KEY) || '';
    } catch {
      last = '';
    }
    setAgentInput(last);
    setAgentModalOpen(true);
  };

  const confirmEnable = async () => {
    const identifier = agentInput.trim();
    if (!identifier) {
      notificationController.error({ message: 'Please enter your name or email.' });
      return;
    }
    setBusy(true);
    try {
      const s = await enableDebugSession(userId, identifier);
      setSession(s);
      try {
        window.localStorage.setItem(AGENT_STORAGE_KEY, identifier);
      } catch {
        // non-fatal
      }
      setAgentModalOpen(false);
      notificationController.success({ message: 'Debug mode enabled. Diagnostics will run when the user opens the app.' });
    } catch (e: any) {
      notificationController.error({ message: e?.message || 'Failed to enable debug mode.' });
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (checked: boolean) => {
    if (checked) {
      openEnablePrompt();
      return;
    }
    if (!session) return;
    setBusy(true);
    try {
      const s = await disableDebugSession(session.id);
      setSession(s);
      notificationController.success({ message: 'Debug mode disabled.' });
    } catch (e: any) {
      notificationController.error({ message: e?.message || 'Failed to disable debug mode.' });
    } finally {
      setBusy(false);
    }
  };

  const enqueue = async (action_type: 'RUN_DIAGNOSTICS' | 'FORCE_SYNC' | 'OTA_INSTALL', params?: Record<string, unknown>) => {
    if (!session) return;
    setBusy(true);
    try {
      await enqueueDebugAction(session.id, { action_type, params });
      notificationController.success({ message: 'Action queued. It will run on the user’s device.' });
      await refresh();
    } catch (e: any) {
      notificationController.error({ message: e?.message || 'Failed to queue action.' });
    } finally {
      setBusy(false);
    }
  };

  const pollAge = secondsAgo(session?.last_app_poll_at ?? null);
  const appConnected = pollAge !== null && pollAge <= 30;

  return (
    <Drawer
      title={`Troubleshooting${userName ? ` — ${userName}` : ''}`}
      visible={open}
      onClose={onClose}
      width={Math.min(760, typeof window !== 'undefined' ? window.innerWidth - 40 : 760)}
      zIndex={1100}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '16px 18px',
              borderRadius: 10,
              border: isActive ? '1px solid #52c41a' : 'var(--soft-panel-border)',
              background: isActive ? 'rgba(82,196,26,0.10)' : 'var(--soft-panel-bg)',
            }}
          >
            <div>
              <Typography.Text strong style={{ fontSize: 17 }}>
                Debug mode
              </Typography.Text>
              <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>
                {isActive ? 'On — remote troubleshooting is live' : 'Off — remote troubleshooting for this customer'}
              </div>
            </div>
            <Switch checked={isActive} loading={busy} onChange={onToggle} />
          </div>

          <div style={{ textAlign: 'right', marginTop: 6 }}>
            <Button type="link" size="small" icon={<QuestionCircleOutlined />} onClick={() => setFaqOpen(true)}>
              Agent guide &amp; FAQ
            </Button>
          </div>

          {isActive && session && (
            <>
              <div style={{ marginTop: 12, fontSize: 13 }}>
                <div>
                  <b>App:</b>{' '}
                  {pollAge === null ? (
                    <Tag color="default">never polled</Tag>
                  ) : (
                    <Tag color={appConnected ? 'green' : 'orange'}>
                      {appConnected ? 'connected' : 'last seen'} {pollAge}s ago
                    </Tag>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>
                  <b>Agent:</b> {session.agent_identifier || '—'}
                </div>
                <div style={{ marginTop: 4 }}>
                  <b>Firmware:</b> {session.last_known_firmware || 'unknown'}
                </div>
                <div style={{ marginTop: 4, opacity: 0.7 }}>Auto-expires {relativeFuture(session.expires_at)}</div>
              </div>

              <Divider />

              <Typography.Text strong>Actions</Typography.Text>
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                <Button type="primary" block disabled={busy} onClick={() => enqueue('RUN_DIAGNOSTICS')}>
                  Run diagnostics
                </Button>
                <Button block disabled={busy} onClick={() => enqueue('FORCE_SYNC')}>
                  Force data sync
                </Button>
                <div style={{ display: 'flex', width: '100%', gap: 8 }}>
                  <Select
                    style={{ flex: 1 }}
                    placeholder="Select firmware version"
                    value={selectedFirmware}
                    onChange={setSelectedFirmware}
                    // Render the dropdown inside the drawer so it isn't hidden
                    // behind the drawer's raised zIndex (it opens over a modal).
                    getPopupContainer={(trigger) => (trigger?.parentElement as HTMLElement) || document.body}
                    options={firmwares.map((f) => ({
                      label: `v${f.version}${f.is_production_file ? ' (production)' : ''}`,
                      value: f.id,
                    }))}
                  />
                  <Button
                    disabled={busy || selectedFirmware === undefined}
                    onClick={() => enqueue('OTA_INSTALL', { firmware_id: selectedFirmware })}
                  >
                    Install
                  </Button>
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Firmware install (upgrade or downgrade) needs the user to tap &quot;Allow&quot; in the app.
                </Typography.Text>
              </Space>

              <Divider />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography.Text strong>Results (latest first)</Typography.Text>
                <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(buildReport(session, userName))}>
                  Copy results
                </Button>
              </div>
              <SessionResults actions={session.actions} openFirst />
            </>
          )}

          {!isActive && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                borderRadius: 8,
                borderLeft: '3px solid #faad14',
                background: 'rgba(250,173,20,0.12)',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 16, marginTop: 2 }} />
              <Typography.Text style={{ fontSize: 13 }}>
                Only turn this on while you&apos;re <b>on a live call with the customer</b>. They&apos;ll get a push and a
                banner in their app. New to this? Open the <b>Agent guide &amp; FAQ</b> above.
              </Typography.Text>
            </div>
          )}

          <Divider />

          {/* Previous sessions — lazily loaded when expanded. */}
          <Collapse onChange={loadPastSessions}>
            <Collapse.Panel
              key="past"
              header={`Previous sessions${pastLoaded && pastSessions.length ? ` (${pastSessions.length})` : ''}`}
            >
              {pastLoading ? (
                <Spin />
              ) : pastSessions.length ? (
                pastSessions.map((s) => (
                  <div key={s.id} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 13 }}>
                        <b>{dayjs(s.created_at).format('MMM D, YYYY h:mm A')}</b>
                        <div style={{ opacity: 0.7 }}>Agent: {s.agent_identifier || '—'}</div>
                      </div>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(buildReport(s, userName))}>
                        Copy
                      </Button>
                    </div>
                    <SessionResults actions={s.actions} />
                  </div>
                ))
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No previous sessions" />
              )}
            </Collapse.Panel>
          </Collapse>
        </>
      )}

      <Modal
        title="Start troubleshooting session"
        visible={agentModalOpen}
        onOk={confirmEnable}
        onCancel={() => setAgentModalOpen(false)}
        okText="Start session"
        confirmLoading={busy}
        zIndex={1200}
      >
        <Typography.Paragraph>Enter your name or email so we know who ran this session.</Typography.Paragraph>
        <Input
          placeholder="e.g. jane@easeseatingsystems.com"
          value={agentInput}
          onChange={(e) => setAgentInput(e.target.value)}
          onPressEnter={confirmEnable}
          autoFocus
        />
      </Modal>

      <Modal
        title="Agent guide & FAQ"
        visible={faqOpen}
        onCancel={() => setFaqOpen(false)}
        footer={<Button onClick={() => setFaqOpen(false)}>Close</Button>}
        zIndex={1200}
        width={620}
      >
        <Collapse accordion>
          {FAQ_ITEMS.map((item, i) => (
            <Collapse.Panel key={String(i)} header={item.q}>
              <Typography.Paragraph style={{ marginBottom: 0 }}>{item.a}</Typography.Paragraph>
            </Collapse.Panel>
          ))}
        </Collapse>
      </Modal>
    </Drawer>
  );
};

export default TroubleshootingDrawer;
